/**
 * ResolutionAuthorizationsSection
 *
 * Collapsible form section for the AddCompanyModal that lets users assign
 * one or more directors (from the current company's director list) as the
 * "authorized persons to give effect" to each resolution.
 *
 * UI Improvements:
 *   - Search / filter across all resolution types
 *   - Completion progress bar (assigned / total)
 *   - Top-level "Quick Assign" bulk action
 *   - Visual polish: category tinting, checkmarks, better collapsed preview
 *
 * Data shape:
 *   authorizations = { [resolutionId]: [ { din, name, designation }, ... ] }
 *
 * Props:
 *   directors            - array of director objects from the company form
 *   authorizations       - { [resolutionId]: [{ din, name, designation }] }
 *   onAuthChange(map)    - callback with the full updated authorizations map
 *   expanded             - whether the entire section is open
 *   onToggleExpanded()   - toggle the section open/closed
 *   expandedCategories   - { [sectionId]: boolean }
 *   onToggleCategory(id) - toggle a single category panel
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { RESOLUTION_SECTIONS } from '../../data/resolutions';

// ── Small reusable icons ─────────────────────────────────────────────────────

function ChevronIcon({ open }) {
  return (
    <svg
      width="10" height="10" viewBox="0 0 10 10"
      className={`transition-transform shrink-0 ${open ? 'rotate-90' : ''}`}
      fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round"
    >
      <path d="M3.5 1.5L7 5L3.5 8.5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="shrink-0">
      <circle cx="6" cy="6" r="5.5" fill="#e8efe5" stroke="#4a6b3f" strokeWidth="0.8" />
      <path d="M3.5 6L5.2 7.7L8.5 4.3" stroke="#4a6b3f" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Multi-select dropdown with checkboxes ────────────────────────────────────

function DirectorMultiSelect({ activeDirectors, selected, onChange, placeholder = '-- Not assigned --', onClearAll }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isSelected = (d) =>
    selected.some((s) => s.name === d.name && (s.din || '') === (d.din || ''));

  const toggle = (d) => {
    if (isSelected(d)) {
      onChange(selected.filter((s) => !(s.name === d.name && (s.din || '') === (d.din || ''))));
    } else {
      onChange([...selected, { din: d.din || '', name: d.name, designation: d.designation || '' }]);
    }
  };

  const label =
    selected.length === 0
      ? placeholder
      : selected.length === 1
        ? selected[0].name
        : `${selected.length} directors`;

  const btnCls =
    'group flex items-center gap-1 border-[1.5px] rounded-lg px-2.5 h-[34px] ' +
    'text-[12px] outline-none cursor-pointer text-left w-full transition-all duration-200 active:scale-[0.98]';

  return (
    <div ref={ref} className="relative" style={{ maxWidth: '240px', minWidth: '170px' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${btnCls} ${open ? 'border-[#145f5f] bg-white shadow-[0_0_0_3px_rgba(20,95,95,0.18)]' : 'border-[#ddd6c6] bg-[#fdfbf7] hover:border-[#cdc4b0]'}`}
        style={{ color: selected.length > 0 ? '#08111f' : '#7a7060' }}
      >
        <span className="flex-1 truncate transition-transform duration-150 group-active:translate-y-[0.5px]">{label}</span>
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <path d="M1.5 3L4 5.5L6.5 3" stroke={open ? '#145f5f' : '#7a7060'} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 z-50 rounded-xl p-1 min-w-[210px]"
          style={{ background: '#fff', border: '1px solid rgba(222,214,198,0.9)', boxShadow: '0 16px 40px -8px rgba(8,17,31,0.20), 0 4px 12px -4px rgba(8,17,31,0.10)', maxHeight: '220px', overflowY: 'auto' }}
        >
          {activeDirectors.length === 0 ? (
            <div className="px-2.5 py-2 text-[12px] text-[#7a7060]">No directors available</div>
          ) : (
            <>
              {activeDirectors.map((d, dIdx) => (
                <label
                  key={dIdx}
                  className="flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer hover:bg-[#faf7f2] text-[12px]"
                >
                  <input
                    type="checkbox"
                    checked={isSelected(d)}
                    onChange={() => toggle(d)}
                    className="w-[15px] h-[15px] m-0 accent-[#145f5f] cursor-pointer shrink-0 block"
                  />
                  <span className="truncate leading-none">
                    {d.name}
                    {d.designation ? <span className="text-[#7a7060]"> ({d.designation})</span> : ''}
                  </span>
                </label>
              ))}
              {(onClearAll || selected.length > 0) && (
                <button
                  type="button"
                  onClick={() => { if (onClearAll) onClearAll(); else onChange([]); setOpen(false); }}
                  className="w-full text-left px-2.5 py-2 rounded-lg text-[10.5px] text-[#8a6a1f] mt-1 hover:bg-[#f3ebd7]"
                >
                  Clear all
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main section component ───────────────────────────────────────────────────

export default function ResolutionAuthorizationsSection({
  directors,
  authorizations,
  onAuthChange,
  expanded,
  onToggleExpanded,
  expandedCategories,
  onToggleCategory,
}) {
  const [searchTerm, setSearchTerm] = useState('');

  // Only directors with a name and without a cessation date are eligible
  const activeDirectors = (directors || []).filter((d) => d.name && !d.cessation);

  // ── Totals ────────────────────────────────────────────────────────────────────
  const totalCount = useMemo(
    () => RESOLUTION_SECTIONS.reduce((sum, s) => sum + s.items.length, 0),
    [],
  );

  const assignedCount = Object.keys(authorizations).filter(
    (k) => Array.isArray(authorizations[k]) && authorizations[k].length > 0,
  ).length;

  const progressPct = totalCount > 0 ? Math.round((assignedCount / totalCount) * 100) : 0;

  // ── Search / filter ───────────────────────────────────────────────────────────
  const lowerSearch = searchTerm.trim().toLowerCase();

  const filteredSections = useMemo(() => {
    if (!lowerSearch) return RESOLUTION_SECTIONS;
    return RESOLUTION_SECTIONS
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) =>
            item.title.toLowerCase().includes(lowerSearch) ||
            (item.subtitle && item.subtitle.toLowerCase().includes(lowerSearch)),
        ),
      }))
      .filter((section) => section.items.length > 0);
  }, [lowerSearch]);

  const matchCount = useMemo(
    () => filteredSections.reduce((sum, s) => sum + s.items.length, 0),
    [filteredSections],
  );

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const getAuthList = (resolutionId) => {
    const val = authorizations[resolutionId];
    if (Array.isArray(val)) return val;
    if (val && typeof val === 'object' && val.name) return [val];
    return [];
  };

  const setAuth = (resolutionId, directorArray) => {
    const next = { ...authorizations };
    if (!directorArray || directorArray.length === 0) {
      delete next[resolutionId];
    } else {
      next[resolutionId] = directorArray;
    }
    onAuthChange(next);
  };

  /** Clear all assignments across every resolution. */
  const clearAll = () => {
    onAuthChange({});
  };

  // ── Bulk multi-select helpers (used by the "Assign all" checkbox dropdowns) ──
  const sameDir = (s, d) => s.name === d.name && (s.din || '') === (d.din || '');
  const normList = (v) => (Array.isArray(v) ? v : v && v.name ? [v] : []);

  // Directors currently assigned to EVERY item in `items` — these show ticked.
  const bulkSelected = (items) =>
    activeDirectors
      .filter((d) => items.length > 0 && items.every((item) => getAuthList(item.id).some((s) => sameDir(s, d))))
      .map((d) => ({ din: d.din || '', name: d.name, designation: d.designation || '' }));

  // Apply the new ticked set to every item: tick → add to all, untick → remove from all.
  const applyBulk = (items, newArr) => {
    const next = { ...authorizations };
    for (const item of items) {
      let arr = normList(next[item.id]);
      for (const d of activeDirectors) {
        const shouldHave = newArr.some((s) => sameDir(s, d));
        const has = arr.some((s) => sameDir(s, d));
        if (shouldHave && !has) arr = [...arr, { din: d.din || '', name: d.name, designation: d.designation || '' }];
        else if (!shouldHave && has) arr = arr.filter((s) => !sameDir(s, d));
      }
      if (arr.length) next[item.id] = arr; else delete next[item.id];
    }
    onAuthChange(next);
  };

  // ── Collapsed preview: show top assigned resolution names ────────────────────
  const collapsedPreview = useMemo(() => {
    if (assignedCount === 0) return 'Optionally assign directors as authorized persons for each resolution type.';
    const assigned = RESOLUTION_SECTIONS
      .flatMap((s) => s.items)
      .filter((item) => getAuthList(item.id).length > 0)
      .slice(0, 3)
      .map((item) => item.title);
    const suffix = assignedCount > 3 ? ` and ${assignedCount - 3} more` : '';
    return `${assigned.join(', ')}${suffix}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizations, assignedCount]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="border-t-2 border-[#ddd6c6] pt-3 mt-1 mb-3">
      {/* Section header */}
      <button
        type="button"
        onClick={onToggleExpanded}
        className="w-full flex items-center gap-2 mb-1 text-left"
      >
        <ChevronIcon open={expanded} />
        <div
          className="text-[18px] font-bold text-[#08111f]"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Resolution Authorizations
        </div>
        {assignedCount > 0 && (
          <div
            className="text-[10px] px-2 py-[2px] rounded-md font-bold"
            style={{ background: '#e4f5f3', color: '#145f5f' }}
          >
            {assignedCount} / {totalCount} assigned
          </div>
        )}
        <span className="ml-auto text-[10px] text-[#7a7060]">
          {expanded ? 'Click to collapse' : 'Click to expand'}
        </span>
      </button>

      {/* Progress bar */}
      {assignedCount > 0 && (
        <div className="mx-4 mb-2 mt-1">
          <div
            className="h-[4px] rounded-full overflow-hidden"
            style={{ background: '#e8e3d8' }}
          >
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${progressPct}%`, background: progressPct === 100 ? '#4a6b3f' : '#145f5f' }}
            />
          </div>
        </div>
      )}

      {/* Collapsed preview */}
      {!expanded && (
        <p className="text-[12.5px] text-[#7a7060] ml-6 mt-1">
          {collapsedPreview}
        </p>
      )}

      {expanded && (
        <>
          <p className="text-[12.5px] text-[#7a7060] mb-2 ml-6">
            Select one or more directors who will be authorized to give effect to
            each resolution. Leave unassigned to use the default signatory.
          </p>

          {activeDirectors.length === 0 ? (
            <div
              className="text-center py-4 rounded-xl border border-dashed text-[12px] text-[#7a7060]"
              style={{ borderColor: '#ddd6c6' }}
            >
              Add directors above first to assign resolution authorizations
            </div>
          ) : (
            <>
              {/* Toolbar: Search + Quick Assign + Clear */}
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                {/* Search input */}
                <div className="relative flex-1" style={{ minWidth: '180px', maxWidth: '320px' }}>
                  <input
                    type="text"
                    placeholder="Search resolutions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full border-[1.5px] border-[#ddd6c6] rounded-lg pl-7 pr-2 h-[34px] text-[12px] text-[#08111f] outline-none bg-[#fdfbf7] hover:border-[#cdc4b0] focus:border-[#145f5f] focus:bg-white focus:shadow-[0_0_0_3px_rgba(20,95,95,0.18)] transition-all duration-200"
                  />
                  <svg
                    width="12" height="12" viewBox="0 0 12 12"
                    className="absolute left-2 top-1/2 -translate-y-1/2"
                    fill="none" stroke="#7a7060" strokeWidth="1.3"
                  >
                    <circle cx="5" cy="5" r="3.5" />
                    <path d="M7.5 7.5L10.5 10.5" strokeLinecap="round" />
                  </svg>
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#7a7060] hover:text-[#8a6a1f]"
                    >
                      &times;
                    </button>
                  )}
                </div>

                {lowerSearch && (
                  <span className="text-[10px] text-[#7a7060]">
                    {matchCount} of {totalCount} match
                  </span>
                )}

                {/* Quick assign to all */}
                <div style={{ width: '190px' }} title="Tick directors to assign to ALL resolutions">
                  <DirectorMultiSelect
                    activeDirectors={activeDirectors}
                    placeholder="Quick assign all…"
                    selected={bulkSelected(RESOLUTION_SECTIONS.flatMap((s) => s.items))}
                    onChange={(arr) => applyBulk(RESOLUTION_SECTIONS.flatMap((s) => s.items), arr)}
                    onClearAll={assignedCount > 0 ? clearAll : undefined}
                  />
                </div>

              </div>

              {/* Category sections */}
              <div className="flex flex-col gap-2">
                {filteredSections.map((section) => {
                  // During search, auto-expand categories with matches
                  const isOpen = lowerSearch ? true : expandedCategories[section.id];
                  const sectionAssigned = section.items.filter(
                    (item) => getAuthList(item.id).length > 0,
                  ).length;
                  const sectionComplete = sectionAssigned === section.items.length;

                  // Category background tint
                  const categoryBg = sectionComplete
                    ? '#e8efe5' // fully assigned — sage
                    : sectionAssigned > 0
                      ? '#f3ebd7' // partially assigned — amber
                      : '#f8f5ef'; // none assigned — default

                  return (
                    <div
                      key={section.id}
                      className="rounded-xl border overflow-hidden"
                      style={{ borderColor: sectionComplete ? '#d3e2cb' : '#ddd6c6' }}
                    >
                      {/* Category header */}
                      <div
                        className="flex items-center gap-2 px-3 py-[9px]"
                        style={{ background: categoryBg }}
                      >
                        <button
                          type="button"
                          onClick={() => !lowerSearch && onToggleCategory(section.id)}
                          className="flex items-center gap-2 flex-1 text-left transition-colors"
                          style={{ color: '#7a7060' }}
                        >
                          {!lowerSearch && <ChevronIcon open={isOpen} />}
                          {sectionComplete && <CheckIcon />}
                          <span className="text-[12px] font-bold text-[#3a3020]">
                            {section.title}
                          </span>
                          {sectionAssigned > 0 && (
                            <span
                              className="text-[9.5px] px-1.5 py-[1px] rounded-md font-bold"
                              style={{
                                background: sectionComplete ? '#d3e2cb' : '#e8efe5',
                                color: sectionComplete ? '#3a5530' : '#4a6b3f',
                              }}
                            >
                              {sectionAssigned}/{section.items.length}
                            </span>
                          )}
                        </button>

                        {/* Bulk-assign dropdown for the category */}
                        {isOpen && (
                          <div style={{ width: '190px' }} title="Tick directors to assign to all resolutions in this category">
                            <DirectorMultiSelect
                              activeDirectors={activeDirectors}
                              placeholder="Assign all…"
                              selected={bulkSelected(section.items)}
                              onChange={(arr) => applyBulk(section.items, arr)}
                            />
                          </div>
                        )}

                        <span className="text-[10px] text-[#7a7060] shrink-0">
                          {section.items.length} resolutions
                        </span>
                      </div>

                      {/* Individual resolution rows */}
                      {isOpen && (
                        <div className="border-t" style={{ borderColor: '#e8e3d8' }}>
                          {section.items.map((item, idx) => (
                            <div
                              key={item.id}
                              className="flex items-center gap-3 px-3 py-[7px]"
                              style={{
                                borderBottom:
                                  idx < section.items.length - 1
                                    ? '1px solid #f0ebe0'
                                    : 'none',
                                background: idx % 2 === 0 ? '#fff' : '#faf9f6',
                              }}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-[11.5px] font-semibold text-[#08111f] truncate">
                                  {item.icon} {item.title}
                                </div>
                                <div className="text-[10px] text-[#7a7060] truncate">
                                  {item.subtitle}
                                </div>
                              </div>
                              <DirectorMultiSelect
                                activeDirectors={activeDirectors}
                                selected={getAuthList(item.id)}
                                onChange={(arr) => setAuth(item.id, arr)}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {lowerSearch && filteredSections.length === 0 && (
                  <div className="text-center py-4 text-[12px] text-[#7a7060]">
                    No resolutions match "{searchTerm}"
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
